import { useState, useEffect, useRef } from 'react';
import { API, getAuthHeader } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MapPin, Navigation, RefreshCw, Loader2, Signal, SignalZero, 
  Play, Pause, Battery, ExternalLink, Lock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export default function RastreioPage() {
  const [traccarDevices, setTraccarDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Pegar usuário logado e suas permissões
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const permissaoRastreio = currentUser.is_super_admin 
    ? 'ver_todos' 
    : (currentUser.permissoes?.rastreio || 'sem_acesso');

  useEffect(() => {
    // Só carrega se tiver permissão
    if (permissaoRastreio !== 'sem_acesso') {
      loadTraccar();
      
      // Auto refresh a cada 10 segundos
      if (autoRefresh) {
        refreshIntervalRef.current = setInterval(loadTraccar, 10000);
      }
    } else {
      setLoading(false);
    }

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [autoRefresh, permissaoRastreio]);

  const loadTraccar = () => {
    axios.get(API + '/traccar/posicoes', { headers: getAuthHeader() })
      .then(r => {
        let lista = Array.isArray(r.data) ? r.data : [];
        
        // Se permissão é "ver_apenas_si", filtrar para mostrar apenas o dispositivo do usuário
        if (permissaoRastreio === 'ver_apenas_si') {
          // Filtra por nome que contenha o nome do usuário ou identificador
          const nomeUsuario = currentUser.name?.toLowerCase() || '';
          lista = lista.filter(d => 
            d.nome?.toLowerCase().includes(nomeUsuario) ||
            d.identificador?.toLowerCase().includes(nomeUsuario.split(' ')[0])
          );
        }
        
        setTraccarDevices(lista);
        setUltimaAtualizacao(new Date());
      })
      .catch(err => {
        console.log('Erro ao carregar Traccar:', err);
        setTraccarDevices([]);
      })
      .finally(() => setLoading(false));
  };

  const abrirMapa = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const formatarTempo = (dataStr) => {
    if (!dataStr) return '-';
    const data = new Date(dataStr);
    const agora = new Date();
    const diffMs = agora - data;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffMs / 60000);
    
    if (diffSec < 10) return 'Agora mesmo';
    if (diffSec < 60) return `${diffSec}s atrás`;
    if (diffMin < 60) return `${diffMin} min atrás`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h atrás`;
    return data.toLocaleDateString('pt-BR');
  };

  // Se não tem permissão, mostrar mensagem de acesso negado
  if (permissaoRastreio === 'sem_acesso') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="rastreio-sem-acesso">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Acesso Restrito</h2>
            <p className="text-slate-500 mb-4">
              Você não tem permissão para visualizar o rastreio GPS.
            </p>
            <p className="text-sm text-slate-400">
              Entre em contato com o administrador para solicitar acesso.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const traccarOnline = traccarDevices.filter(d => d.online || d.status === 'online');
  const traccarOffline = traccarDevices.filter(d => !d.online && d.status !== 'online');

  return (
    <div className="space-y-6" data-testid="rastreio-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Navigation className="w-8 h-8 text-green-600" />
            Rastreio GPS - Traccar
          </h1>
          <p className="text-slate-500">
            {permissaoRastreio === 'ver_apenas_si' 
              ? 'Visualizando apenas sua localização'
              : 'Acompanhe a localização da equipe em tempo real'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={loadTraccar}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
          >
            {autoRefresh ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
            Auto
          </Button>
        </div>
      </div>

      {/* Aviso de permissão limitada */}
      {permissaoRastreio === 'ver_apenas_si' && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">Você está visualizando apenas sua própria localização</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Signal className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{traccarOnline.length}</p>
                <p className="text-xs text-slate-500">Online</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <SignalZero className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{traccarOffline.length}</p>
                <p className="text-xs text-slate-500">Offline</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Navigation className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{traccarDevices.length}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${autoRefresh ? 'bg-green-100' : 'bg-orange-100'}`}>
                <RefreshCw className={`w-5 h-5 ${autoRefresh ? 'text-green-600' : 'text-orange-600'}`} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">{autoRefresh ? 'Ligado' : 'Desligado'}</p>
                <p className="text-xs text-slate-500">Auto Refresh</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Última atualização */}
      {ultimaAtualizacao && (
        <p className="text-xs text-slate-400 text-right">
          Última atualização: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}
        </p>
      )}

      {/* Lista de Dispositivos Traccar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Navigation className="w-5 h-5 text-green-600" />
            Dispositivos Traccar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : traccarDevices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Navigation className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">
                {permissaoRastreio === 'ver_apenas_si' 
                  ? 'Seu dispositivo não foi encontrado'
                  : 'Nenhum dispositivo encontrado'
                }
              </p>
              <p className="text-sm mt-2">
                {permissaoRastreio === 'ver_apenas_si'
                  ? 'Verifique se o app Traccar está configurado com seu nome'
                  : 'Configure o app Traccar Client nos celulares dos técnicos'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Online primeiro */}
              {traccarOnline.map(device => (
                <DeviceCard key={device.id} device={device} onOpenMap={abrirMapa} formatTime={formatarTempo} />
              ))}
              
              {/* Offline depois */}
              {traccarOffline.map(device => (
                <DeviceCard key={device.id} device={device} onOpenMap={abrirMapa} formatTime={formatarTempo} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link para Traccar - Só mostra para quem pode ver todos */}
      {permissaoRastreio === 'ver_todos' && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800">Gerenciar dispositivos no Traccar</p>
                <p className="text-sm text-green-600">Adicione novos dispositivos ou visualize o histórico completo</p>
              </div>
              <Button 
                variant="outline" 
                className="border-green-300 text-green-700 hover:bg-green-100"
                onClick={() => window.open('https://demo.traccar.org', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Abrir Traccar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Componente de Card do Dispositivo
function DeviceCard({ device, onOpenMap, formatTime }) {
  const online = device.online || device.status === 'online';
  
  return (
    <div 
      className={`p-4 rounded-lg border-2 transition-all ${
        online 
          ? 'border-green-200 bg-green-50 hover:border-green-300' 
          : 'border-slate-200 bg-slate-50 hover:border-slate-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            online ? 'bg-green-600' : 'bg-slate-400'
          }`}>
            <Navigation className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-lg">{device.nome}</p>
            <div className="flex items-center gap-3 text-sm mt-1">
              <span className={`flex items-center gap-1 font-medium ${
                online ? 'text-green-600' : 'text-slate-400'
              }`}>
                {online ? <Signal className="w-4 h-4" /> : <SignalZero className="w-4 h-4" />}
                {online ? 'Online' : 'Offline'}
              </span>
              {device.bateria !== null && device.bateria !== undefined && (
                <span className={`flex items-center gap-1 ${
                  device.bateria < 20 ? 'text-red-500' : 'text-slate-500'
                }`}>
                  <Battery className="w-4 h-4" />
                  {device.bateria}%
                </span>
              )}
              {device.velocidade !== undefined && device.velocidade > 0.5 && (
                <span className="text-blue-600">
                  🚗 {(device.velocidade * 1.852).toFixed(0)} km/h
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          {device.latitude && device.longitude && (
            <Button 
              size="sm" 
              onClick={() => onOpenMap(device.latitude, device.longitude)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <MapPin className="w-4 h-4 mr-1" />
              Ver no Mapa
            </Button>
          )}
          <p className="text-xs text-slate-400 mt-2">
            {device.data_posicao ? formatTime(device.data_posicao) : 'Sem posição'}
          </p>
        </div>
      </div>
      
      {/* Coordenadas */}
      {device.latitude && device.longitude && (
        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-sm text-slate-500">
          <span>
            📍 {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
          </span>
          {device.precisao && (
            <span>Precisão: {device.precisao.toFixed(0)}m</span>
          )}
        </div>
      )}
    </div>
  );
}
